import { ApiError } from '@/shared/api/api-client';

/**
 * Device booking refusals (Scheduling PR #1964, S12). The server answers 400
 * `ValidationProblemDetails` — `{ errors: { <Key>: ["<message>"] } }` — where
 * the message is the `ErrorCodes.Slot.*` constant's TEXT and the key is the
 * offending field (`DeviceId`, `DeviceProfileId`, `BillingProfileId`,
 * `Recurring`, `ResourceIds`, `Invoice`, `ResourceBooked`). We classify by
 * message first (unambiguous) and fall back to the key.
 */
export type DeviceBookingErrorCode =
  | 'DeviceBookingHasProvider'
  | 'DeviceBookingHasResourceIds'
  | 'DeviceBookingNeedsProfile'
  | 'DeviceBookingNotRecurring'
  | 'DeviceBookingNeedsBillingProfile'
  | 'DeviceBookingInvoiceFailed'
  | 'ResourceNotSchedulable'
  | 'ResourceBooked'
  | 'RecurringPartiallyBooked'
  | 'Unknown';

export interface DeviceBookingFailure {
  code: DeviceBookingErrorCode;
  /** What the user sees. */
  message: string;
  /** Verbatim server text when there was one. */
  serverMessage: string | null;
  status: number | null;
  /**
   * PROD-2383 — only on `RecurringPartiallyBooked`: the sessions of the course
   * that WERE booked before one failed. They are kept by the server, so the
   * caller must treat the course as partly on the calendar, never as a clean
   * refusal.
   */
  bookedAppointmentIds?: string[];
}

/** User-facing copy per refusal — clear, actionable, no backend jargon. */
export const DEVICE_BOOKING_MESSAGES: Record<DeviceBookingErrorCode, string> = {
  DeviceBookingHasProvider:
    "A device booking can't include a practitioner. Remove the provider and try again.",
  DeviceBookingHasResourceIds: "A device booking can't add other devices.",
  DeviceBookingNeedsProfile: "Pick one of the device's products first.",
  // PROD-2383: a device course CAN recur now, on the device route; the provider
  // route still refuses one (DeviceRecurringOnProviderRoute). Only an out-of-date
  // caller can see this.
  DeviceBookingNotRecurring: 'A recurring device course is booked on the device route — refresh and try again.',
  DeviceBookingNeedsBillingProfile:
    'Add at least one service so the booking can be invoiced.',
  DeviceBookingInvoiceFailed:
    "The invoice couldn't be created, so the booking was not saved. Try again.",
  ResourceNotSchedulable:
    "This device isn't bookable here — the product is not live or the device has no working hours.",
  ResourceBooked: 'That window was just taken. Pick another time.',
  RecurringPartiallyBooked:
    'Only part of the course was booked. The booked sessions were kept — check the calendar before booking the rest.',
  Unknown: "The device couldn't be booked.",
};

/** Server message fragments (lower-case) → code. Order matters: most specific first. */
const MESSAGE_RULES: ReadonlyArray<readonly [string, DeviceBookingErrorCode]> = [
  ['cannot name a practitioner', 'DeviceBookingHasProvider'],
  ['cannot name additional devices', 'DeviceBookingHasResourceIds'],
  ["must name one of the device's appointment profiles", 'DeviceBookingNeedsProfile'],
  ['cannot be recurring', 'DeviceBookingNotRecurring'],
  ['needs a billing profile or added services', 'DeviceBookingNeedsBillingProfile'],
  ['invoice could not be created', 'DeviceBookingInvoiceFailed'],
  ['not bookable at this practice', 'ResourceNotSchedulable'],
  ['resource already booked', 'ResourceBooked'],
];

/** PROD-2383 — the keys Scheduling reports a partially booked course under (`ErrorCodes.Recurring`). */
const PARTIAL_KEY = 'recurring.partiallybooked';
const PARTIAL_IDS_KEY = 'recurring.bookedappointmentids';

/** Key → code, when the message did not match (defensive against copy edits). */
const KEY_RULES: Record<string, DeviceBookingErrorCode> = {
  resourcebooked: 'ResourceBooked',
  billingprofileid: 'DeviceBookingNeedsBillingProfile',
  deviceprofileid: 'ResourceNotSchedulable',
  deviceid: 'ResourceNotSchedulable',
  invoice: 'DeviceBookingInvoiceFailed',
  recurring: 'DeviceBookingNotRecurring',
  resourceids: 'DeviceBookingHasResourceIds',
};

interface ProblemDetails {
  errors?: Record<string, unknown>;
  title?: string;
  messages?: unknown;
}

function readProblem(body: string): { entries: Array<[string, string]>; title: string | null } {
  let parsed: ProblemDetails | null = null;
  try {
    parsed = JSON.parse(body) as ProblemDetails;
  } catch {
    return { entries: [], title: body.trim() || null };
  }
  const entries: Array<[string, string]> = [];
  if (parsed?.errors && typeof parsed.errors === 'object') {
    for (const [key, value] of Object.entries(parsed.errors)) {
      const list = Array.isArray(value) ? value : [value];
      for (const v of list) {
        const text = String(v ?? '').trim();
        if (text) entries.push([key, text]);
      }
    }
  }
  if (entries.length === 0 && Array.isArray(parsed?.messages)) {
    for (const m of parsed.messages) {
      const text = String(m ?? '').trim();
      if (text) entries.push(['', text]);
    }
  }
  return { entries, title: parsed?.title?.trim() || null };
}

/** Classify one `(key, message)` pair. */
export function classifyDeviceBookingMessage(
  key: string,
  message: string,
): DeviceBookingErrorCode | null {
  const m = message.toLowerCase();
  for (const [fragment, code] of MESSAGE_RULES) {
    if (m.includes(fragment)) return code;
  }
  return KEY_RULES[key.toLowerCase()] ?? null;
}

/**
 * Map any thrown value from the booking mutation to a user-facing failure.
 * Non-400 errors (network, 5xx) come back as `Unknown` with the generic copy.
 */
export function classifyDeviceBookingFailure(err: unknown): DeviceBookingFailure {
  if (!(err instanceof ApiError)) {
    return { code: 'Unknown', message: DEVICE_BOOKING_MESSAGES.Unknown, serverMessage: null, status: null };
  }
  const { entries, title } = readProblem(err.body ?? '');

  // PROD-2383 — a recurring course that stopped part-way. Checked FIRST: the
  // same body also carries the failed session's own refusal (e.g.
  // ResourceBooked), and reporting only that would read as "nothing was
  // booked" over a course that is half on the calendar. The server's sentence
  // says how many sessions were booked and which one failed; the failed
  // session's reason is appended so the operator knows why.
  const partial = entries.find(([key]) => key.toLowerCase() === PARTIAL_KEY);
  if (partial) {
    const booked = entries
      .filter(([key]) => key.toLowerCase() === PARTIAL_IDS_KEY)
      .map(([, id]) => id);
    const reason = entries
      .filter(([key]) => {
        const k = key.toLowerCase();
        return k !== PARTIAL_KEY && k !== PARTIAL_IDS_KEY;
      })
      .map(([key, message]) => classifyDeviceBookingMessage(key, message))
      .find((c): c is DeviceBookingErrorCode => !!c);
    return {
      code: 'RecurringPartiallyBooked',
      message: reason ? `${partial[1]} ${DEVICE_BOOKING_MESSAGES[reason]}` : partial[1],
      serverMessage: partial[1],
      status: err.status,
      bookedAppointmentIds: booked,
    };
  }

  for (const [key, message] of entries) {
    const code = classifyDeviceBookingMessage(key, message);
    if (code) {
      return { code, message: DEVICE_BOOKING_MESSAGES[code], serverMessage: message, status: err.status };
    }
  }
  const serverMessage = entries[0]?.[1] ?? title;
  return {
    code: 'Unknown',
    message: serverMessage && err.status === 400 ? serverMessage : DEVICE_BOOKING_MESSAGES.Unknown,
    serverMessage: serverMessage ?? null,
    status: err.status,
  };
}
